import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Pause, AlertCircle, Clock, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface PausedTicket {
  id: string;
  customerName?: string | null;
  vehicleInfo?: string;
  pauseReason?: string | null;
  pausedAt?: string | null;
  reactivateAt?: string | null;
}

interface PausedWorkBadgeProps {
  pausedTickets: PausedTicket[];
  className?: string;
}

/** Urgency color map based on pause reason */
function getReasonColor(reason: string | null | undefined, reactivateAt: string | null | undefined): string {
  if (!reason) return 'bg-muted text-muted-foreground';
  const r = reason.toLowerCase();
  
  // Red: action required now
  if (r.includes('customer approval') || r.includes('awaiting diagnostics')) {
    return 'bg-destructive/15 text-destructive border-destructive/30';
  }
  
  // Amber: delayed / waiting
  if (r.includes('waiting for parts') || r.includes('blocked')) {
    // Flashing amber if reactivate_at is past due
    if (reactivateAt && new Date(reactivateAt) < new Date()) {
      return 'bg-amber-500/20 text-amber-700 border-amber-500/40 animate-pulse';
    }
    return 'bg-amber-500/15 text-amber-600 border-amber-500/30';
  }
  
  // Gray: informational
  return 'bg-muted text-muted-foreground';
}

function getReasonIcon(reason: string | null | undefined) {
  if (!reason) return Pause;
  const r = reason.toLowerCase();
  if (r.includes('customer approval') || r.includes('awaiting diagnostics')) return AlertCircle;
  if (r.includes('waiting for parts')) return Package;
  if (r.includes('break')) return Clock;
  return Pause;
}

export function PausedWorkBadge({ pausedTickets, className }: PausedWorkBadgeProps) {
  const [open, setOpen] = useState(false);
  
  if (pausedTickets.length === 0) return null;

  // Group by reason
  const groups = new Map<string, PausedTicket[]>();
  for (const t of pausedTickets) {
    const key = t.pauseReason || 'Other';
    const existing = groups.get(key) || [];
    existing.push(t);
    groups.set(key, existing);
  }

  // Determine overall urgency for the badge pill
  const hasUrgent = pausedTickets.some(t => {
    const r = (t.pauseReason || '').toLowerCase();
    return r.includes('customer approval') || r.includes('awaiting diagnostics');
  });
  const hasOverdue = pausedTickets.some(t => 
    t.reactivateAt && new Date(t.reactivateAt) < new Date()
  );

  const pillColor = hasUrgent
    ? 'bg-destructive/15 text-destructive border-destructive/30'
    : hasOverdue
      ? 'bg-amber-500/20 text-amber-700 border-amber-500/40 animate-pulse'
      : 'bg-muted text-muted-foreground';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Badge 
          variant="outline" 
          className={cn(
            'cursor-pointer gap-1 text-xs font-medium px-1.5 py-0.5',
            pillColor,
            className
          )}
        >
          <Pause className="h-3 w-3" />
          {pausedTickets.length}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="text-xs font-semibold text-muted-foreground mb-2">
          Paused Work ({pausedTickets.length})
        </div>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {Array.from(groups.entries()).map(([reason, tickets]) => {
            const ReasonIcon = getReasonIcon(reason);
            return (
              <div key={reason} className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  <ReasonIcon className="h-3 w-3" />
                  <span>{reason}</span>
                  <Badge variant="secondary" className="ml-auto text-[10px] px-1 py-0">
                    {tickets.length}
                  </Badge>
                </div>
                {tickets.map(t => (
                  <div
                    key={t.id}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-xs',
                      getReasonColor(t.pauseReason, t.reactivateAt)
                    )}
                  >
                    <div className="font-medium truncate">
                      {t.customerName || 'Unknown'} {t.vehicleInfo ? `· ${t.vehicleInfo}` : ''}
                    </div>
                    <div className="text-[10px] opacity-70 mt-0.5">
                      {t.pausedAt 
                        ? `Paused ${formatDistanceToNow(new Date(t.pausedAt), { addSuffix: true })}`
                        : 'Recently paused'
                      }
                      {t.reactivateAt && (
                        <span className="ml-1">
                          · Follow-up {new Date(t.reactivateAt) < new Date() 
                            ? 'overdue' 
                            : formatDistanceToNow(new Date(t.reactivateAt), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
